import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../hooks/useAlert';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';


const CreateMeetingModal = ({ onClose }) => {
    const { user } = useAuth();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [roomType, setRoomType] = useState('private');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const modalRef = useRef(null);
    const firstFieldRef = useRef(null);

    useEffect(() => {
        if (firstFieldRef.current) {
            firstFieldRef.current.focus();
        }
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            // --- FIX: The path should be '/meetings', not '/api/meetings' ---
            // The '/api' prefix is already handled by the axios instance in api.js
            const response = await api.post('/meetings', {
                name,
                description,
                room_type: roomType,
            });
            showAlert('Meeting created successfully!', 'success');
            onClose();
            navigate(`/meet/${response.data.id}`);
        } catch (error) {
            showAlert(error.response?.data?.message || 'Failed to create meeting.');
            setIsSubmitting(false);
        }
    };

    const styles = {
        overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
        modal: { background: '#1F2937', padding: '2rem', borderRadius: '8px', width: '90%', maxWidth: '500px', color: 'white' },
        formGroup: { margin: '1rem 0' },
        label: { display: 'block', marginBottom: '0.5rem' },
        input: { width: '100%', padding: '0.8rem', borderRadius: '5px', border: '1px solid #4B5563', background: '#374151', color: 'white', fontSize: '1rem' },
        button: { padding: '0.8rem 1.5rem', cursor: 'pointer', border: 'none', borderRadius: '5px', color: 'white', fontSize: '1rem' },
    };

    return (
        <div 
            style={styles.overlay} 
            onClick={onClose} 
            role="dialog" 
            aria-modal="true" 
            aria-labelledby="modal-title"
        >
            <div style={styles.modal} ref={modalRef} onClick={e => e.stopPropagation()}>
                <h2 id="modal-title">Create a New Meeting</h2>
                <form onSubmit={handleCreate}>
                    <div style={styles.formGroup}>
                        <label htmlFor="meeting-name" style={styles.label}>Meeting Name (Required)</label>
                        <input 
                            id="meeting-name"
                            type="text" 
                            value={name} 
                            onChange={e => setName(e.target.value)} 
                            ref={firstFieldRef}
                            style={styles.input}
                            required 
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label htmlFor="meeting-description" style={styles.label}>Description (Optional)</label>
                        <textarea 
                            id="meeting-description"
                            value={description} 
                            onChange={e => setDescription(e.target.value)} 
                            style={{ ...styles.input, height: '80px' }}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label htmlFor="room-type" style={styles.label}>Room Type</label>
                        <select 
                            id="room-type"
                            value={roomType} 
                            onChange={e => setRoomType(e.target.value)} 
                            style={styles.input}
                        >
                            <option value="private">Private (Only invited users)</option>
                            <option value="public">Public (Any logged-in user can join)</option>
                            <option value="open">Open (Guests can join)</option>
                        </select>
                    </div>
                    <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: '#4B5563' }}>Cancel</button>
                        <button type="submit" disabled={isSubmitting} style={{ ...styles.button, background: '#16A34A' }}>
                            {isSubmitting ? 'Creating...' : 'Create Meeting'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateMeetingModal;
